/**
 * PauseMenu - Pause menu overlay during gameplay
 */

import React, { useState, useEffect } from 'react';
import { playUIClick, playUIHover } from '../../game/audio/SoundEffects';
import { audioManager } from '../../engine/AudioManager';

interface PauseMenuProps {
  onResume?: () => void;
  onSave?: () => void;
  onSettings?: () => void;
  onMainMenu?: () => void;
  visible?: boolean;
  isSaved?: boolean; // Track if game has unsaved changes
  showSettingsOnly?: boolean; // If true, show settings directly (for main menu)
}

export const PauseMenu: React.FC<PauseMenuProps> = ({
  onResume,
  onSave,
  onSettings,
  onMainMenu,
  visible = false,
  isSaved = true,
  showSettingsOnly = false,
}) => {
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [showOptions, setShowOptions] = useState(showSettingsOnly); // Auto-open if showSettingsOnly
  const [showQuitWarning, setShowQuitWarning] = useState(false);
  const [masterVolume, setMasterVolume] = useState(100);
  const [musicVolume, setMusicVolume] = useState(100);
  const [sfxVolume, setSfxVolume] = useState(100);

  // Auto-open settings when showSettingsOnly changes
  useEffect(() => {
    if (showSettingsOnly) {
      setShowOptions(true);
    }
  }, [showSettingsOnly]);

  // Reset to main pause menu when pause menu closes
  useEffect(() => {
    if (!visible && !showSettingsOnly) {
      setShowOptions(false);
      setShowQuitWarning(false);
    }
  }, [visible, showSettingsOnly]);

  // Initialize volume sliders from AudioManager
  useEffect(() => {
    const currentVolumes = audioManager.getVolumes();
    setMasterVolume(Math.round(currentVolumes.master * 100));
    setMusicVolume(Math.round(currentVolumes.music * 100));
    setSfxVolume(Math.round(currentVolumes.sfx * 100));
  }, [visible]);

  // Handle master volume change
  const handleMasterVolumeChange = (value: number) => {
    setMasterVolume(value);
    audioManager.setMasterVolume(value / 100); // Convert to 0-1 range
  };

  // Handle music volume change
  const handleMusicVolumeChange = (value: number) => {
    setMusicVolume(value);
    audioManager.setMusicVolume(value / 100); // Convert to 0-1 range
  };

  // Handle SFX volume change
  const handleSfxVolumeChange = (value: number) => {
    setSfxVolume(value);
    audioManager.setSFXVolume(value / 100); // Convert to 0-1 range
  };

  if (!visible && !showQuitWarning) return null;

  const handleHover = (buttonId: string) => {
    setHoveredButton(buttonId);
    playUIHover();
  };

  const handleClick = (callback?: () => void) => {
    playUIClick();
    callback?.();
  };

  const handleQuitClick = () => {
    playUIClick();
    if (!isSaved) {
      setShowQuitWarning(true);
    } else {
      onMainMenu?.();
    }
  };

  const handleConfirmQuit = () => {
    playUIClick();
    setShowQuitWarning(false);
    onMainMenu?.();
  };

  const handleCancelQuit = () => {
    playUIClick();
    setShowQuitWarning(false);
  };

  const handleOptionsClick = () => {
    playUIClick();
    setShowOptions(true);
  };

  const handleBackFromOptions = () => {
    playUIClick();
    if (showSettingsOnly) {
      // If opened from main menu, close settings by calling onSettings
      onSettings?.();
    } else {
      // If opened from pause menu, just close settings overlay
      setShowOptions(false);
    }
  };

  // Quit Warning Modal
  if (showQuitWarning) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
      }}>
        <div style={{
          backgroundColor: 'rgba(20, 20, 40, 0.95)',
          border: '3px solid #ff0000',
          borderRadius: '10px',
          padding: '40px',
          boxShadow: '0 0 30px rgba(255, 0, 0, 0.5)',
          textAlign: 'center',
          fontFamily: 'monospace',
        }}>
          <h2 style={{
            fontSize: '32px',
            color: '#ff0000',
            marginBottom: '20px',
            letterSpacing: '3px',
          }}>
            ⚠️ WARNING
          </h2>
          <p style={{
            fontSize: '18px',
            color: '#ffaa00',
            marginBottom: '30px',
          }}>
            Your game is unsaved!<br />
            Are you sure you want to quit?
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
          }}>
            <button
              onClick={handleCancelQuit}
              onMouseEnter={() => {
                setHoveredButton('cancel-quit');
                playUIHover();
              }}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                backgroundColor: hoveredButton === 'cancel-quit' ? 'rgba(0, 170, 255, 0.3)' : 'rgba(0, 0, 0, 0.8)',
                color: hoveredButton === 'cancel-quit' ? '#00ff00' : '#00aaff',
                border: `3px solid ${hoveredButton === 'cancel-quit' ? '#00ff00' : '#00aaff'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                letterSpacing: '2px',
              }}
            >
              CANCEL
            </button>
            <button
              onClick={handleConfirmQuit}
              onMouseEnter={() => {
                setHoveredButton('confirm-quit');
                playUIHover();
              }}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                backgroundColor: hoveredButton === 'confirm-quit' ? 'rgba(255, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.8)',
                color: '#ff0000',
                border: '3px solid #ff0000',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                letterSpacing: '2px',
              }}
            >
              QUIT
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Options Menu
  if (showOptions) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
      }}>
        <div style={{
          backgroundColor: 'rgba(20, 20, 40, 0.95)',
          border: '3px solid #00aaff',
          borderRadius: '10px',
          padding: '40px',
          boxShadow: '0 0 30px rgba(0, 170, 255, 0.5)',
          width: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          fontFamily: 'monospace',
        }}>
          <h2 style={{
            fontSize: '32px',
            color: '#00aaff',
            marginBottom: '30px',
            textAlign: 'center',
            letterSpacing: '3px',
          }}>
            SETTINGS
          </h2>

          {/* Audio Settings Section */}
          <div style={{
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            border: '2px solid #00ff00',
          }}>
            <h3 style={{
              fontSize: '20px',
              color: '#00ff00',
              marginBottom: '20px',
              letterSpacing: '2px',
            }}>
              🔊 AUDIO
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                color: '#00ff00',
                marginBottom: '8px',
              }}>
                Master Volume: {masterVolume}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={masterVolume}
                onChange={(e) => handleMasterVolumeChange(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                color: '#00ff00',
                marginBottom: '8px',
              }}>
                Music Volume: {musicVolume}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={musicVolume}
                onChange={(e) => handleMusicVolumeChange(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                color: '#00ff00',
                marginBottom: '8px',
              }}>
                SFX Volume: {sfxVolume}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={sfxVolume}
                onChange={(e) => handleSfxVolumeChange(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              />
            </div>
          </div>

          {/* Control Mapping Section */}
          <div style={{
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            border: '2px solid #ffaa00',
          }}>
            <h3 style={{
              fontSize: '20px',
              color: '#ffaa00',
              marginBottom: '20px',
              letterSpacing: '2px',
            }}>
              🎮 CONTROLS
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px',
              fontSize: '14px',
            }}>
              <div style={{ color: '#ffffff' }}>Move Left:</div>
              <div style={{ color: '#ffaa00' }}>A or ←</div>

              <div style={{ color: '#ffffff' }}>Move Right:</div>
              <div style={{ color: '#ffaa00' }}>D or →</div>

              <div style={{ color: '#ffffff' }}>Jump:</div>
              <div style={{ color: '#ffaa00' }}>W or SPACE</div>

              <div style={{ color: '#ffffff' }}>Crouch:</div>
              <div style={{ color: '#ffaa00' }}>S or ↓</div>

              <div style={{ color: '#ffffff' }}>Sprint:</div>
              <div style={{ color: '#ffaa00' }}>SHIFT</div>

              <div style={{ color: '#ffffff' }}>Flashlight:</div>
              <div style={{ color: '#ffaa00' }}>F</div>

              <div style={{ color: '#ffffff' }}>Mask:</div>
              <div style={{ color: '#ffaa00' }}>M</div>

              <div style={{ color: '#ffffff' }}>Interact:</div>
              <div style={{ color: '#ffaa00' }}>E</div>

              <div style={{ color: '#ffffff' }}>Pause:</div>
              <div style={{ color: '#ffaa00' }}>ESC</div>
            </div>
          </div>

          <button
            onClick={handleBackFromOptions}
            onMouseEnter={() => {
              setHoveredButton('back');
              playUIHover();
            }}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              padding: '12px 50px',
              fontSize: '18px',
              fontWeight: 'bold',
              fontFamily: 'monospace',
              backgroundColor: hoveredButton === 'back' ? 'rgba(0, 170, 255, 0.3)' : 'rgba(0, 0, 0, 0.8)',
              color: hoveredButton === 'back' ? '#00ff00' : '#00aaff',
              border: '3px solid #00aaff',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              letterSpacing: '2px',
              margin: '0 auto',
              display: 'block',
            }}
          >
            BACK
          </button>
        </div>
      </div>
    );
  }

  const menuButtons = [
    { id: 'resume', label: 'RESUME', onClick: onResume, color: '#00ff00' },
    { id: 'save', label: 'SAVE GAME', onClick: onSave, color: '#ffaa00' },
    { id: 'options', label: 'OPTIONS', onClick: handleOptionsClick, color: '#00aaff' },
    { id: 'main-menu', label: 'QUIT TO MENU', onClick: handleQuitClick, color: '#ff0000' },
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      fontFamily: 'monospace',
      color: '#ffffff',
      zIndex: 2000,
      backdropFilter: 'blur(5px)',
    }}>
      {/* Centered content container */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {/* Title */}
        <div style={{
          marginBottom: '40px',
          textAlign: 'center',
        }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: '0 0 20px #ffffff',
            marginBottom: '10px',
            letterSpacing: '8px',
          }}>
            PAUSED
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#888',
            letterSpacing: '2px',
          }}>
            Press ESC to resume
          </p>
        </div>

        {/* Menu buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
        }}>
          {menuButtons.map((button) => (
            <button
              key={button.id}
              onMouseEnter={() => handleHover(button.id)}
              onMouseLeave={() => setHoveredButton(null)}
              onClick={() => handleClick(button.onClick)}
              style={{
                padding: '12px 50px',
                fontSize: '20px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                backgroundColor: hoveredButton === button.id
                  ? `${button.color}33`
                  : 'rgba(0, 0, 0, 0.7)',
                color: hoveredButton === button.id ? '#ffffff' : button.color,
                border: `2px solid ${button.color}`,
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textShadow: hoveredButton === button.id
                  ? `0 0 10px ${button.color}`
                  : 'none',
                boxShadow: hoveredButton === button.id
                  ? `0 0 20px ${button.color}66`
                  : 'none',
                transform: hoveredButton === button.id
                  ? 'scale(1.05)'
                  : 'scale(1)',
                letterSpacing: '2px',
              }}
            >
              {button.label}
            </button>
          ))}
        </div>
      </div>

      {/* Warning for main menu - positioned absolutely below buttons */}
      {hoveredButton === 'main-menu' && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, 220px)', // Position well below the buttons with extra spacing
          padding: '10px 20px',
          backgroundColor: 'rgba(255, 0, 0, 0.2)',
          border: '2px solid #ff0000',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#ff0000',
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}>
          ⚠️ Unsaved progress will be lost!
        </div>
      )}
    </div>
  );
};
